import React, { FunctionComponent, memo, useCallback } from 'react';
import { Box, Checkbox, Grid, Link, Radio, Typography } from '@material-ui/core';
import Image from 'material-ui-image';
import OptionUnavailable from '../../../../../../gqlCommon/assets/images/optionUnavailable.png';
import IFeature from '../../../../../model/IFeature';
import ConfigurablePrice from '../../../../presentational/prices/configurablePrice/ConfigurablePrice';
import featureOptionStyle from './featureOptionStyle';
import clsx from 'clsx';
import ICategoryAttributes from '../../../../../model/ICategoryAttributes';
import featuresPerRowService from '../../../../../service/featuresPerRowService';
import { FeatureType } from '../../../../../enum/FeatureType';
import Translation from '../../../../../../gqlCommon/components/translation/Translation';
import CategoryPurpose from '../../../../../enum/CategoryPurpose';
import CategoryFeatureTypes from '../../../../../enum/CategoryFeatureTypes';
import SpecificationDisclaimer from '../../../../../../gqlCommon/components/presentation/specificationDisclaimer/SpecificationDisclaimer';
import IndicativeNumber from '../../../../../../common/components/indicativeNumber/IndicativeNumber';
import { AddOnsTypes } from '../../../../../enum/AddOnsTypes';
import useScrollToSection from '../../../../../hooks/navigation/useScrollToSection';
import usePlatformConfig from '../../../../../../common/react/usePlatformConfig';
import { getTranslation } from '../../../../../../gqlCommon/utils/getTranslation';

export interface IFeatureProps {
  feature: IFeature;
  onClick: (id: string, category?: string, featureName?: string) => void;
  id: string;
  isSelected: boolean;
  categoryAttributes?: ICategoryAttributes;
  openAccessoryDetailsModal: (value: boolean, feature: IFeature) => void;
}

const FeatureOption: FunctionComponent<IFeatureProps> = memo(
  ({ feature, onClick, id, isSelected, categoryAttributes, openAccessoryDetailsModal }) => {
    const styles = featureOptionStyle();
    const displaylabelofselectedoptiononcategory = categoryAttributes?.displaylabelofselectedoptiononcategory;
    const numberofitemsperrow = categoryAttributes?.numberofitemsperrow;
    const gridSize = featuresPerRowService.getFeaturesPerRow(numberofitemsperrow);
    const isDisabled = feature.attributes.isDefaultItem || false;
    const { scrollToDisclaimer } = useScrollToSection();
    const {
      platformConfig: { showExtraDetailsInNewTab },
    } = usePlatformConfig();
    const extraDetailsUrl = getTranslation('trans__configurator__customise_page_service_section__show_features_url');

    const setDataOption = useCallback((optionName: string) => {
      if (optionName === CategoryPurpose.EXTERIOR_COLOUR) {
        return CategoryFeatureTypes.EXTERIOR;
      } else if (optionName === CategoryPurpose.INTERIOR_COLOUR) {
        return CategoryFeatureTypes.INTERIOR;
      }
      return CategoryFeatureTypes.ACCESSORIES;
    }, []);

    const handleOnClickExtraDetails = (ev: React.MouseEvent<HTMLAnchorElement>) => {
      ev.stopPropagation();
      if (showExtraDetailsInNewTab) {
        return window.open(extraDetailsUrl);
      }

      return openAccessoryDetailsModal(true, feature);
    };

    return (
      <Grid
        item={true}
        xs={gridSize ?? 4}
        className={clsx([
          styles.gridContainer,
          {
            [styles.selectedFeatureContainer]: isSelected,
          },
        ])}
      >
        {isDisabled && <Box className={styles.backdrop} data-test={`option:disabled:${feature.code}`} />}
        <Box
          className={clsx([
            styles.itemContainer,
            {
              [styles.itemContainerDisabled]: isDisabled,
            },
          ])}
          onClick={() => onClick(id, categoryAttributes?.categorypurpose, feature?.name)}
          data-test={`option:${feature.code}`}
        >
          <Image
            key={feature.images[0]?.url}
            src={feature.images[0]?.url || 'error'}
            alt={feature.name}
            className={styles.image}
            animationDuration={0}
            color={'transparent'}
            aspectRatio={1.75}
            data-test={'option:image'}
            errorIcon={
              <img
                src={OptionUnavailable}
                alt={`${feature.name}_error`}
                className={styles.imageUnavailable}
                data-test={'image:vehicle:unavailable'}
              />
            }
          />
          {categoryAttributes?.displayfeaturename && (
            <Box className={styles.metaContainer}>
              <Typography
                component="p"
                variant="caption"
                className={styles.name}
                data-test={'option:name'}
                {...(isSelected && { 'data-option': `${setDataOption(categoryAttributes?.categorypurpose)}:selected` })}
              >
                {feature.name}

                {feature.name === AddOnsTypes.GUARANTEED_COE && (
                  <IndicativeNumber
                    dataTest="guaranteed_coe"
                    displayNumber={<Translation id="trans__configurator__indicative_numbers__option" />}
                    onClick={scrollToDisclaimer}
                  />
                )}
              </Typography>
              {feature.attributes?.disclaimer && (
                <SpecificationDisclaimer disclaimer={feature.attributes?.disclaimer} />
              )}
              {feature.attributes.extradetails && (
                <Link
                  className={styles.moreDetailsToggle}
                  variant={'helpingText'}
                  underline={'always'}
                  color={'secondary'}
                  data-test={'button:accessory:modal'}
                  onClick={(ev: React.MouseEvent<HTMLAnchorElement>) => handleOnClickExtraDetails(ev)}
                >
                  <Translation id="trans__configurator__customise_page_feature_extra_details__details_link" />
                </Link>
              )}
            </Box>
          )}
          <Box
            className={clsx([
              styles.additionalInfoContainer,
              {
                [styles.withoutBorder]: isSelected,
                [styles.selectionContainer]: displaylabelofselectedoptiononcategory,
              },
            ])}
          >
            {categoryAttributes?.displayfeatureprice && (
              <ConfigurablePrice
                prices={feature.prices}
                display={{ showOrLabel: false }}
                classes={{ single: { priceText: styles.priceText } }}
              />
            )}
            {!categoryAttributes.selectiontype || categoryAttributes.selectiontype === FeatureType.multi ? (
              <Checkbox
                checked={isSelected}
                size={'medium'}
                color={'primary'}
                disabled={isDisabled}
                data-test={`option:checkbox:${feature.code}`}
              />
            ) : (
              <Radio
                className={styles.option}
                checked={isSelected}
                value={id}
                disabled={isDisabled}
                edge={'end'}
                color={'primary'}
                data-test={`option:radio`}
              />
            )}
          </Box>
        </Box>
      </Grid>
    );
  },
);

export default FeatureOption;
